import { useState } from 'react'
import './CreatePartnerModal.css'
import './DeletePartnerModal.css'
import { Partner, PartnerService } from '@/services/PartnerService'

interface DeletePartnerModalProps {
  partner: Partner
  onClose: () => void
  onSuccess: () => void
}

export default function DeletePartnerModal({ partner, onClose, onSuccess }: DeletePartnerModalProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    setIsDeleting(true)
    setError(null)

    try {
      await PartnerService.delete(partner.id)
      onSuccess()
      onClose()
    } catch (err) {
      console.error('Error deleting partner:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete channel partner')
      setIsDeleting(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="create-partner-modal-content">
        <div className="modal-header">
          <h2>Delete Channel Partner</h2>
          <p>This action cannot be undone</p>
          <button className="modal-close" onClick={onClose} disabled={isDeleting}>×</button>
        </div>

        <div className="delete-partner-content">
          <div className="warning-box">
            <span className="warning-icon">⚠️</span>
            <div className="warning-text">
              <p className="warning-title">You are about to delete this channel partner:</p>
              <p className="warning-partner-name">{partner.name}</p>
              <p className="warning-partner-id">Partner ID: {partner.id}</p>
            </div>
          </div>

          <div className="delete-info">
            <p>This will permanently delete:</p>
            <ul>
              <li>The partner record and all associated data</li>
              <li>All configurations, custom fields, and SIM providers</li>
              <li>The partner's database and Keycloak group and authentication settings</li>
            </ul>
            <p className="delete-note">
              <strong>Note:</strong> If this partner has any devices, the deletion will fail.
              You must remove all devices before deleting this partner.
            </p>
            <p className="delete-warning">
              <strong>Warning:</strong> This action cannot be undone. All data will be permanently lost.
            </p>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-delete"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Partner'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
